import { useEffect, useMemo, useRef } from "react";
import { AgGridReact } from "ag-grid-react";
import { IGetRowsParams, IDatasource } from "ag-grid-community";
import { useTravelRequestSearchFilters } from "../../hooks/use-travel-request-search-filters";
import { useDebouncedValue } from "@hooks";
import {
  BookingKitQueryInput,
  useTravelRequestSearchLazyQuery,
  useTravelRequestSearchTotalLazyQuery
} from "@bonliva-crm/graphql";
import { TravelRequestSortBy, SortOrder } from "@bonliva-crm/shared";

type Props = {
  getGridRef: () => AgGridReact;
};

export function useTravelRequestTableDatasource(props: Props) {
  const { getGridRef } = props;

  const {
    queryInput,
    filterManager: {
      formMethods: { watch }
    }
  } = useTravelRequestSearchFilters();

  const debouncedInput = useDebouncedValue(queryInput, 400);

  const queryInputRef = useRef<BookingKitQueryInput>();

  const [getItems] = useTravelRequestSearchLazyQuery();
  const [getTotal] = useTravelRequestSearchTotalLazyQuery();


  useEffect(() => {
    queryInputRef.current = queryInput;
  }, [queryInput]);

  const sortValue = watch("sort");

  const getSort = () => {
    switch (sortValue) {
      case "createdOn_asc":
        return { sortBy: TravelRequestSortBy.CreatedOn, sortOrder: SortOrder.Asc };
      case "createdOn_desc":
        return { sortBy: TravelRequestSortBy.CreatedOn, sortOrder: SortOrder.Desc };
      case "departureDate_asc":
        return {
          sortBy: TravelRequestSortBy.FirstDepartureDate,
          sortOrder: SortOrder.Asc
        };
      case "departureDate_desc":
        return {
          sortBy: TravelRequestSortBy.FirstDepartureDate,
          sortOrder: SortOrder.Desc
        };

      default:
        console.log("Unknown sort value", sortValue);
    }
  };

  const getRows = (rowParams: IGetRowsParams) => {
    const filter = queryInputRef.current;
    // const filter = {};

    const limit = rowParams.endRow - rowParams.startRow;
    const skip = rowParams.startRow;

    Promise.all([
      getItems({
        variables: {
          limit,
          skip,
          filter,
          ...getSort()
        },
        fetchPolicy: "no-cache"
      }),
      getTotal({
        variables: { filter },
        fetchPolicy: "no-cache"
      })
    ])
      .then((res) => {
        rowParams.successCallback(
          res[0].data.travelRequestSearch.items,
          res[1].data.travelRequestSearchTotal.total
        );
      })
      .catch((err) => {
        console.error(err);
        rowParams.failCallback();
      });
  };

  const dataSource: IDatasource = useMemo(() => {
    return {
      rowCount: undefined,
      getRows,
      destroy: () => {
        const grid = getGridRef();
        if (!grid?.api?.isDestroyed()) {
          grid?.api.getSelectedNodes()?.forEach((node) => {
            node.setSelected(false);
          });
        }
      }
    };
  }, [debouncedInput]);

  return dataSource;
}
